import { LoadingOutlined } from "@ant-design/icons";
import type { SelectProps } from "antd";
import { Empty, Flex, Select, Spin, Typography } from "antd";
import type { DefaultOptionType } from "antd/es/select";
import React, { useMemo } from "react";
import {
    Controller,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
    useFormContext,
} from "react-hook-form";
import styled from "styled-components";

import { PrimaryText } from "@/shared/ui/Typography";

const StyledSelect = styled(Select<string>)`
    width: 100%;

    .ant-select-selection-search-input {
        display: block;
    }
`;

interface Option {
    value: string;
    label: string;
    disabled?: boolean;
}

interface ActionOption {
    label: string;
    onClick: () => void;
}

interface Props<TFieldValues extends FieldValues> {
    name: FieldPath<TFieldValues>;
    options: ReadonlyArray<Option>;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
    allowClear?: boolean;
    actionOption?: ActionOption;
    onAfterChange?: (opt?: Option | null) => void;
    onResetOnChange?: (newValue: string, previousValue: string | null) => void;
    getOptionDisabled?: (option: Option) => boolean;
    onInputChange?: (value: string) => void;
    style?: React.CSSProperties;
    className?: string;
    selectProps?: Omit<
        SelectProps,
        | "value"
        | "onChange"
        | "disabled"
        | "loading"
        | "placeholder"
        | "allowClear"
        | "options"
    >;
}

export const RHFAutocomplete = <TFieldValues extends FieldValues>({
    name,
    options,
    label,
    placeholder,
    disabled = false,
    loading = false,
    rules,
    allowClear = true,
    actionOption,
    getOptionDisabled,
    onAfterChange,
    onResetOnChange,
    onInputChange,
    style,
    className,
    selectProps,
}: Props<TFieldValues>) => {
    const { control, formState, trigger } = useFormContext<TFieldValues>();

    const allDisabled = disabled || formState.isSubmitting;

    const byValue = useMemo(() => {
        const m = new Map<string, Option>();
        options.forEach((o) => m.set(o.value, o));
        return m;
    }, [options]);

    const ACTION_VALUE = "__action__";

    const antOptions = useMemo(() => {
        const baseOptions: DefaultOptionType[] = options.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: getOptionDisabled ? getOptionDisabled(opt) : false,
        }));

        if (actionOption) {
            baseOptions.push({
                value: ACTION_VALUE,
                label: (
                    <Typography.Link
                        onClick={(e) => {
                            e.preventDefault();
                            actionOption.onClick();
                        }}
                    >
                        {actionOption.label}
                    </Typography.Link>
                ),
            });
        }

        return baseOptions;
    }, [options, actionOption, getOptionDisabled, ACTION_VALUE]);

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => {
                return (
                    <div className={className} style={style}>
                        {label && (
                            <div style={{ marginBottom: 4 }}>
                                <label>{label}</label>
                            </div>
                        )}
                        <StyledSelect
                            {...selectProps}
                            value={field.value || undefined}
                            onChange={(value: string) => {
                                if (value === ACTION_VALUE) {
                                    actionOption?.onClick();
                                    return;
                                }

                                const previousValue = field.value ?? null;
                                const newValue = value || null;

                                if (
                                    newValue !== previousValue &&
                                    onResetOnChange
                                ) {
                                    onResetOnChange(newValue, previousValue);
                                }

                                field.onChange(newValue);
                                if (fieldState.error) {
                                    void trigger(name);
                                }

                                const selectedOption = byValue.get(value);
                                onAfterChange?.(selectedOption ?? null);
                            }}
                            onBlur={field.onBlur}
                            placeholder={placeholder}
                            disabled={allDisabled}
                            allowClear={allowClear}
                            loading={loading}
                            options={loading ? [] : antOptions}
                            showSearch
                            optionRender={(option) => (
                                <PrimaryText>{option.data.label}</PrimaryText>
                            )}
                            onSearch={(value) => {
                                onInputChange?.(value);
                            }}
                            filterOption={(input, option) => {
                                if (option?.value === ACTION_VALUE) return true;

                                return (
                                    option?.label
                                        ?.toString()
                                        .toLowerCase()
                                        .includes(input.toLowerCase()) ?? false
                                );
                            }}
                            status={fieldState.error ? "error" : undefined}
                            notFoundContent={
                                loading ? (
                                    <Flex
                                        gap={8}
                                        align="center"
                                        justify="center"
                                    >
                                        <Spin
                                            indicator={<LoadingOutlined spin />}
                                            size="small"
                                        />

                                        <PrimaryText textColor="disabled">
                                            Завантаження...
                                        </PrimaryText>
                                    </Flex>
                                ) : (
                                    <Empty description={'"Немає даних"'} />
                                )
                            }
                        />
                        {fieldState.error && (
                            <PrimaryText
                                textColor="error"
                                style={{
                                    marginTop: 4,
                                }}
                            >
                                {fieldState.error.message}
                            </PrimaryText>
                        )}
                    </div>
                );
            }}
        />
    );
};
